import { useMemo, useState, useCallback } from "react";
import { Html } from "@react-three/drei";
import {
  BufferGeometry,
  Float32BufferAttribute,
  Vector3,
  Matrix4,
  DoubleSide,
  TextureLoader,
  Mesh,
  SRGBColorSpace,
  ShaderMaterial,
  ClampToEdgeWrapping,
  LinearFilter,
  Raycaster,
} from "three";
import type { CenterlineData } from "./useTunnelCenterline";
import type { ProfileAssignment } from "./tunnelProfileShapes";
import { getProfileAtStation } from "./tunnelProfileShapes";
import type { TunnelPhoto } from "./tunnelPhotos";
import { savePhotoMetas, deletePhotoImage } from "./tunnelPhotos";
import { useProject } from "./ProjectContext";
import { tunnelPoint3DToWorld } from "./drawings3D";

interface TunnelPhotos3DProps {
  data: CenterlineData;
  profileAssignments: ProfileAssignment[];
  photos: TunnelPhoto[];
  onPhotosChange: (photos: TunnelPhoto[]) => void;
}

const FRAME_SAMPLES = 2000;
const RING_SPACING = 2;
/** How far (meters) from the photo station to include tunnel geometry */
const CLIP_RADIUS = 30;

/**
 * Build tunnel surface geometry clipped to a station range.
 * If minStation/maxStation are omitted, builds the full tunnel.
 */
function buildTunnelGeometry(
  data: CenterlineData,
  profileAssignments: ProfileAssignment[],
  frames: { normals: Vector3[]; binormals: Vector3[] },
  curvePoints: Vector3[],
  minStation?: number,
  maxStation?: number,
): BufferGeometry {
  const totalLength = data.totalLength;
  const fullRingCount = Math.floor(totalLength / RING_SPACING) + 1;

  // Determine ring index range
  const iStart = minStation !== undefined ? Math.max(0, Math.floor(minStation / RING_SPACING)) : 0;
  const iEnd = maxStation !== undefined ? Math.min(fullRingCount - 1, Math.ceil(maxStation / RING_SPACING)) : fullRingCount - 1;

  const rings: Vector3[][] = [];
  for (let i = iStart; i <= iEnd; i++) {
    const station = i * RING_SPACING;
    const t = Math.min(station / totalLength, 1);
    const frameIdx = Math.min(Math.round(t * FRAME_SAMPLES), FRAME_SAMPLES);

    const center = curvePoints[frameIdx];
    const normal = frames.normals[frameIdx];
    const binormal = frames.binormals[frameIdx];

    const profile = getProfileAtStation(station, profileAssignments);
    const ring = profile.points.map(
      (p2d) =>
        new Vector3(
          center.x + p2d.x * binormal.x - p2d.y * normal.x,
          center.y + p2d.x * binormal.y - p2d.y * normal.y,
          center.z + p2d.x * binormal.z - p2d.y * normal.z,
        ),
    );
    rings.push(ring);
  }

  const positions: number[] = [];
  for (let i = 0; i < rings.length - 1; i++) {
    const rA = rings[i];
    const rB = rings[i + 1];
    const count = Math.min(rA.length, rB.length);
    for (let j = 0; j < count - 1; j++) {
      const a = rA[j], b = rA[j + 1], c = rB[j], d = rB[j + 1];
      positions.push(a.x, a.y, a.z, c.x, c.y, c.z, b.x, b.y, b.z);
      positions.push(b.x, b.y, b.z, c.x, c.y, c.z, d.x, d.y, d.z);
    }
  }

  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geo.computeVertexNormals();
  return geo;
}

/**
 * Compute a corner world position by unprojecting NDC through the inverse projView matrix
 * and raycasting against the tunnel mesh.
 */
function computeCornerPosition(
  projViewMatrix: number[],
  camPos: Vector3,
  tunnelMesh: Mesh,
  ndcX: number,
  ndcY: number,
): Vector3 | null {
  const invProjView = new Matrix4();
  invProjView.fromArray(projViewMatrix);
  invProjView.invert();

  const nearWorld = new Vector3(ndcX, ndcY, -1).applyMatrix4(invProjView);
  const farWorld = new Vector3(ndcX, ndcY, 1).applyMatrix4(invProjView);
  const dir = farWorld.sub(nearWorld).normalize();

  const raycaster = new Raycaster();
  raycaster.set(camPos, dir);
  const hits = raycaster.intersectObject(tunnelMesh, false);
  if (hits.length > 0) {
    return hits[0].point.clone();
  }
  return null;
}

// Projective texture vertex shader
const projVertexShader = /* glsl */ `
  uniform mat4 uProjViewMatrix;
  uniform vec3 uCamPos;
  varying vec4 vProjCoord;
  varying vec3 vWorldPos;
  varying vec3 vToCam;

  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vWorldPos = worldPos.xyz;
    vToCam = uCamPos - worldPos.xyz;
    vProjCoord = uProjViewMatrix * worldPos;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Full image fragment shader
const projFragmentShader = /* glsl */ `
  uniform sampler2D uTexture;
  uniform vec3 uCamDir;
  uniform float uOpacity;
  varying vec4 vProjCoord;
  varying vec3 vWorldPos;
  varying vec3 vToCam;

  void main() {
    vec3 ndc = vProjCoord.xyz / vProjCoord.w;
    if (ndc.x < -1.0 || ndc.x > 1.0 || ndc.y < -1.0 || ndc.y > 1.0 || ndc.z < -1.0 || ndc.z > 1.0) discard;
    vec3 toCamNorm = normalize(vToCam);
    if (dot(toCamNorm, uCamDir) > 0.0) discard;

    vec2 uv = ndc.xy * 0.5 + 0.5;

    float edgeFade = 1.0;
    float margin = 0.02;
    edgeFade *= smoothstep(0.0, margin, uv.x) * smoothstep(0.0, margin, 1.0 - uv.x);
    edgeFade *= smoothstep(0.0, margin, uv.y) * smoothstep(0.0, margin, 1.0 - uv.y);

    vec4 texColor = texture2D(uTexture, uv);
    gl_FragColor = vec4(texColor.rgb, texColor.a * edgeFade * uOpacity);
  }
`;

// Outline-only fragment shader — fixed screen-space pixel width
const outlineFragmentShader = /* glsl */ `
  uniform vec3 uCamDir;
  uniform vec3 uColor;
  uniform float uOpacity;
  varying vec4 vProjCoord;
  varying vec3 vWorldPos;
  varying vec3 vToCam;

  void main() {
    vec3 ndc = vProjCoord.xyz / vProjCoord.w;
    if (ndc.x < -1.0 || ndc.x > 1.0 || ndc.y < -1.0 || ndc.y > 1.0 || ndc.z < -1.0 || ndc.z > 1.0) discard;
    vec3 toCamNorm = normalize(vToCam);
    if (dot(toCamNorm, uCamDir) > 0.0) discard;

    vec2 uv = ndc.xy * 0.5 + 0.5;

    // Use screen-space derivatives to get a fixed ~2px border regardless of distance
    float bx = fwidth(uv.x) * 2.0;
    float by = fwidth(uv.y) * 2.0;
    bool nearEdge = uv.x < bx || uv.x > 1.0 - bx ||
                    uv.y < by || uv.y > 1.0 - by;
    if (!nearEdge) discard;

    gl_FragColor = vec4(uColor, uOpacity);
  }
`;

/** Single photo rendered via projective texture mapping on clipped tunnel geometry */
function PhotoProjection({
  photo,
  data,
  profileAssignments,
  frames,
  curvePoints,
  tunnelMesh,
  isSelected,
  hidden,
  photoNumber,
  worldPos,
  onSelect,
  onToggleHide,
  onDelete,
}: {
  photo: TunnelPhoto;
  data: CenterlineData;
  profileAssignments: ProfileAssignment[];
  frames: { normals: Vector3[]; binormals: Vector3[] };
  curvePoints: Vector3[];
  tunnelMesh: Mesh;
  isSelected: boolean;
  hidden: boolean;
  photoNumber: number;
  worldPos: Vector3;
  onSelect: () => void;
  onToggleHide: () => void;
  onDelete: () => void;
}) {
  // Build clipped geometry for this photo only
  const clippedGeo = useMemo(() => {
    return buildTunnelGeometry(
      data,
      profileAssignments,
      frames,
      curvePoints,
      photo.station - CLIP_RADIUS,
      photo.station + CLIP_RADIUS,
    );
  }, [data, profileAssignments, frames, curvePoints, photo.station]);

  const { material, outlineMaterial, selectedMaterial } = useMemo(() => {
    if (!photo.projViewMatrix || photo.camX === undefined)
      return { material: null, outlineMaterial: null, selectedMaterial: null };

    const loader = new TextureLoader();
    const tex = loader.load(photo.dataUrl);
    tex.colorSpace = SRGBColorSpace;
    tex.wrapS = ClampToEdgeWrapping;
    tex.wrapT = ClampToEdgeWrapping;
    tex.minFilter = LinearFilter;
    tex.magFilter = LinearFilter;

    const projViewMat = new Matrix4();
    projViewMat.fromArray(photo.projViewMatrix);
    const camPos = new Vector3(photo.camX!, photo.camY!, photo.camZ!);
    const camDir = new Vector3(photo.dirX, photo.dirY, photo.dirZ).normalize();

    const mat = new ShaderMaterial({
      uniforms: {
        uTexture: { value: tex },
        uProjViewMatrix: { value: projViewMat },
        uCamPos: { value: camPos },
        uCamDir: { value: camDir },
        uOpacity: { value: 0.9 },
      },
      vertexShader: projVertexShader,
      fragmentShader: projFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -4,
    });

    const outline = new ShaderMaterial({
      uniforms: {
        uProjViewMatrix: { value: projViewMat },
        uCamPos: { value: camPos },
        uCamDir: { value: camDir },
        uColor: { value: new Vector3(0.2, 0.8, 1) },
        uOpacity: { value: 0.6 },
      },
      vertexShader: projVertexShader,
      fragmentShader: outlineFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -5,
    });

    const selected = new ShaderMaterial({
      uniforms: {
        uProjViewMatrix: { value: projViewMat },
        uCamPos: { value: camPos },
        uCamDir: { value: camDir },
        uColor: { value: new Vector3(1, 0.8, 0) },
        uOpacity: { value: 0.5 },
      },
      vertexShader: projVertexShader,
      fragmentShader: outlineFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -6,
    });

    return { material: mat, outlineMaterial: outline, selectedMaterial: selected };
  }, [photo]);

  // Compute top-left corner position for the label
  const topLeftPos = useMemo(() => {
    if (!photo.projViewMatrix || photo.camX === undefined) return worldPos;
    const camPos = new Vector3(photo.camX!, photo.camY!, photo.camZ!);
    const corner = computeCornerPosition(photo.projViewMatrix, camPos, tunnelMesh, -1, 1);
    return corner ?? worldPos;
  }, [photo, tunnelMesh, worldPos]);

  if (!material) return null;

  return (
    <group renderOrder={-1}>
      {/* Show full image when not hidden */}
      {!hidden && <mesh geometry={clippedGeo} material={material} renderOrder={-1} />}

      {/* Always show outline */}
      <mesh geometry={clippedGeo} material={outlineMaterial!} renderOrder={-1} />

      {/* Selection highlight border */}
      {isSelected && <mesh geometry={clippedGeo} material={selectedMaterial!} renderOrder={-1} />}

      {/* Number label + controls at top-left corner */}
      <Html position={[topLeftPos.x, topLeftPos.y, topLeftPos.z]} center={false} zIndexRange={[50, 0]}>
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          transform: "translate(-100%, -100%)",
          pointerEvents: "auto",
        }}>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <button
              onClick={(e) => { e.stopPropagation(); onSelect(); }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 8px",
                borderRadius: 12,
                border: isSelected ? "2px solid #ffcc00" : "1.5px solid rgba(255,255,255,0.7)",
                background: isSelected ? "rgba(255,204,0,0.25)" : "rgba(0,0,0,0.5)",
                backdropFilter: "blur(4px)",
                cursor: "pointer",
                boxShadow: isSelected ? "0 0 10px rgba(255,204,0,0.5)" : "0 2px 6px rgba(0,0,0,0.4)",
                transition: "all 0.15s ease",
                color: isSelected ? "#ffcc00" : "white",
                fontSize: 13,
                fontWeight: 700,
                fontFamily: "system-ui, sans-serif",
                lineHeight: 1,
              }}
              title={`Photo ${photoNumber} @ ${photo.station.toFixed(0)}m`}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
                <circle cx="12" cy="13.5" r="3" />
              </svg>
              {photoNumber}
            </button>

            {isSelected && (
              <button
                onClick={(e) => { e.stopPropagation(); onToggleHide(); }}
                style={{
                  width: 26, height: 26,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: hidden ? "rgba(100,100,255,0.7)" : "rgba(0,0,0,0.5)",
                  color: "#fff",
                  border: "1.5px solid rgba(255,255,255,0.5)",
                  borderRadius: "50%", cursor: "pointer", padding: 0,
                  backdropFilter: "blur(4px)",
                }}
                title={hidden ? "Show photo" : "Hide photo"}
              >
                {hidden ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                )}
              </button>
            )}

            {isSelected && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const a = document.createElement("a");
                  a.href = photo.dataUrl;
                  const ext = photo.dataUrl.startsWith("data:image/png") ? "png" : "jpg";
                  a.download = `photo-${photoNumber}-station-${Math.round(photo.station)}m.${ext}`;
                  a.click();
                }}
                style={{
                  width: 26, height: 26,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  border: "1.5px solid rgba(255,255,255,0.5)",
                  borderRadius: "50%", cursor: "pointer", padding: 0,
                  backdropFilter: "blur(4px)",
                }}
                title="Download photo"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              </button>
            )}

            {isSelected && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                style={{
                  width: 26, height: 26,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "rgba(180, 40, 40, 0.9)",
                  color: "#fff",
                  border: "1.5px solid rgba(255,100,100,0.6)",
                  borderRadius: "50%", cursor: "pointer", padding: 0,
                  backdropFilter: "blur(4px)",
                }}
                title="Delete photo"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                  <path d="M10 11v6M14 11v6" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </Html>
    </group>
  );
}

export function TunnelPhotos3D({ data, profileAssignments, photos, onPhotosChange }: TunnelPhotos3DProps) {
  const project = useProject();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());

  const { frames, curvePoints } = useMemo(() => {
    if (photos.length === 0) return { frames: null, curvePoints: null };
    const f = data.curve.computeFrenetFrames(FRAME_SAMPLES, false);
    const pts = data.curve.getSpacedPoints(FRAME_SAMPLES);
    return { frames: f, curvePoints: pts };
  }, [data, photos.length === 0]);

  // Full tunnel mesh — used only for raycasting corner positions (not rendered)
  const tunnelMesh = useMemo(() => {
    if (!frames || !curvePoints) return null;
    const geo = buildTunnelGeometry(data, profileAssignments, frames, curvePoints);
    const m = new Mesh(geo);
    m.updateMatrixWorld(true);
    return m;
  }, [data, profileAssignments, frames, curvePoints]);

  const photoWorldPositions = useMemo(() => {
    if (!frames || !curvePoints) return [];
    return photos.map((p) => {
      if (p.hitX !== undefined && p.hitY !== undefined && p.hitZ !== undefined) {
        return new Vector3(p.hitX, p.hitY, p.hitZ);
      }
      return tunnelPoint3DToWorld(
        { station: p.station, u: p.hitU, v: p.hitV },
        data,
        frames,
        curvePoints,
        FRAME_SAMPLES,
      );
    });
  }, [photos, data, frames, curvePoints]);

  const deletePhoto = useCallback((id: string) => {
    const updated = photos.filter((p) => p.id !== id);
    onPhotosChange(updated);
    savePhotoMetas(project.id, updated.map(({ dataUrl: _, ...m }) => m));
    deletePhotoImage(project.id, id).catch(() => {});
    setSelectedId(null);
  }, [photos, onPhotosChange]);

  const toggleHide = useCallback((id: string) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (!tunnelMesh || !frames || !curvePoints || photos.length === 0) return null;

  return (
    <group>
      {photos.map((photo, i) => {
        const worldPos = photoWorldPositions[i];
        const isSelected = photo.id === selectedId;
        const hidden = hiddenIds.has(photo.id);
        return (
          <PhotoProjection
            key={photo.id}
            photo={photo}
            data={data}
            profileAssignments={profileAssignments}
            frames={frames}
            curvePoints={curvePoints}
            tunnelMesh={tunnelMesh}
            isSelected={isSelected}
            hidden={hidden}
            photoNumber={i + 1}
            worldPos={worldPos}
            onSelect={() => setSelectedId(isSelected ? null : photo.id)}
            onToggleHide={() => toggleHide(photo.id)}
            onDelete={() => deletePhoto(photo.id)}
          />
        );
      })}
    </group>
  );
}
